/* eslint-disable react-hooks/exhaustive-deps */
import React, { useMemo, useEffect, useRef, forwardRef } from 'react';
import PropTypes from 'prop-types';
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  useTable,
  useSortBy,
  useRowSelect,
  useFilters,
  usePagination
} from 'react-table';

import { downloadTableState } from './config';

const IndeterminateCheckbox = forwardRef(({ indeterminate, ...rest }, ref) => {
  const defaultRef = useRef();
  const resolvedRef = ref || defaultRef;

  useEffect(() => {
    resolvedRef.current.indeterminate = indeterminate;
  }, [resolvedRef, indeterminate]);

  return (
    <>
      <input type="checkbox" ref={resolvedRef} {...rest} />
    </>
  );
});

const Table = ({ data, headerFormatter, defaultSorts, onSelectCheckbox }) => {
  const rowData = useMemo(() => data, [data]);
  const columns = useMemo(() => headerFormatter, [headerFormatter]);

  const initialState = {
    pageSize: 50
  };
  if (defaultSorts.length) {
    initialState.sortBy = defaultSorts.map(id => ({ id, desc: true }));
  }

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    rows,
    prepareRow,

    // pagination
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,

    state: { selectedRowIds, pageIndex, pageSize, filters }
  } = useTable(
    {
      columns,
      data: rowData,
      initialState
    },
    useFilters,
    useSortBy,
    usePagination,
    useRowSelect,
    hooks => {
      hooks.visibleColumns.push(columns => [
        {
          id: 'selection',
          Header: ({ getToggleAllRowsSelectedProps }) => (
            <div className="cvmi-admin-user-table__checkbox">
              <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
            </div>
          ),
          Cell: ({ row }) => (
            <div className="cvmi-admin-user-table__checkbox">
              <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
            </div>
          )
        },
        ...columns
      ]);
    }
  );

  const renderSortIcon = column => {
    if (!column.isSorted) {
      return null;
    }

    return column.isSortedDesc ? (
      <FontAwesomeIcon
        className="tvt-tabular-data-table__sort-icon"
        icon="caret-down"
        size="1x"
      />
    ) : (
      <FontAwesomeIcon
        className="tvt-tabular-data-table__sort-icon"
        icon="caret-up"
        size="1x"
      />
    );
  };

  const onClickDownload = () => {
    downloadTableState(rows, filters);
  };

  useEffect(() => {
    onSelectCheckbox(Object.keys(selectedRowIds).map(rid => data[rid]));
  }, [selectedRowIds]);

  return (
    <div className="cvmi-admin-user-table__container">
      <OverlayTrigger
        placement="right"
        overlay={
          <Tooltip id="admin-modal__item-tooltip">
            <div>Download table state</div>
          </Tooltip>
        }
      >
        <Button onClick={onClickDownload} className="tvt-module-export">
          <FontAwesomeIcon icon="download" />
        </Button>
      </OverlayTrigger>
      <div {...getTableProps()} className="tvt-tabular-data-table">
        <div className="cvmi-admin-user-table__header">
          {headerGroups.map(headerGroup => (
            <div {...headerGroup.getHeaderGroupProps()} className="tr">
              {headerGroup.headers.map(column => (
                <div
                  {...column.getHeaderProps(
                    column.getSortByToggleProps({
                      title: column.hoverTitle || 'Sort'
                    })
                  )}
                  className={`th ${column.className ||
                    'cvmi-admin-user-table__checkbox'}`}
                >
                  {column.render('Header')}
                  {renderSortIcon(column)}
                  <div>{column.canFilter ? column.render('Filter') : null}</div>
                </div>
              ))}
            </div>
          ))}
        </div>
        <div {...getTableBodyProps()}>
          {page.map(row => {
            prepareRow(row);
            return (
              <div className="tr" key={row.index}>
                {row.cells.map((cell, i) => (
                  <div
                    {...cell.getCellProps()}
                    className={`td ${cell.column.className ||
                      'cvmi-admin-user-table__checkbox'}`}
                  >
                    {cell.render('Cell')}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>
      <div className="pagination">
        <button onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
          {'<<'}
        </button>{' '}
        <button onClick={() => previousPage()} disabled={!canPreviousPage}>
          {'<'}
        </button>{' '}
        <button onClick={() => nextPage()} disabled={!canNextPage}>
          {'>'}
        </button>{' '}
        <button onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>
          {'>>'}
        </button>{' '}
        <span>
          Page{' '}
          <strong>
            {pageIndex + 1} of {pageOptions.length}
          </strong>{' '}
        </span>
        <span>
          | Go to page:{' '}
          <input
            type="number"
            defaultValue={pageIndex + 1}
            onChange={e => {
              const page = e.target.value ? Number(e.target.value) - 1 : 0;
              gotoPage(page);
            }}
            style={{ width: '100px' }}
          />
        </span>{' '}
        <select
          value={pageSize}
          onChange={e => {
            setPageSize(Number(e.target.value));
          }}
        >
          {[10, 20, 30, 40, 50].map(pageSize => (
            <option key={pageSize} value={pageSize}>
              Show {pageSize}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

Table.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  headerFormatter: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  width: PropTypes.number,
  height: PropTypes.number,
  rowHeight: PropTypes.number,
  defaultSorts: PropTypes.arrayOf(PropTypes.string),
  showRank: PropTypes.bool
};

Table.defaultProps = {
  width: 1160,
  height: 600,
  rowHeight: 40,
  defaultSorts: [],
  showRank: false
};

export default Table;
